import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Star, Package, Filter, Grid, List, Users, Award, CheckCircle } from 'lucide-react';
import { sellers, getSellersByCity, getSellersByProduct } from '../data/sellers';
import { apparelSellers, searchApparelSellers, getApparelSellersByIndustryType, getApparelSellersByProduct } from '../data/apparelSellers';

const SellersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('rating');
  const [filteredSellers, setFilteredSellers] = useState([...sellers, ...apparelSellers]);

  // Get unique cities
  const cities = Array.from(new Set([
    ...sellers.map(s => s.city),
    ...apparelSellers.map(s => s.city)
  ])).sort();

  // Get unique product categories
  const categories = [
    'Jute Products',
    'Handicrafts',
    'Terracotta',
    'Leather Goods',
    'Baskets',
    'Rugs',
    'Clay Tiles',
    'Garments',
    'Apparel',
    'Knitwear',
    'Woven Garments',
    'Sweaters',
    'T-shirts',
    'Shirts',
    'Trousers'
  ];

  // Filter sellers based on search, city, and category
  useEffect(() => {
    let filtered = [...sellers, ...apparelSellers];

    if (searchQuery) {
      const jute = sellers.filter(seller =>
        seller.organization.toLowerCase().includes(searchQuery.toLowerCase()) ||
        seller.products.toLowerCase().includes(searchQuery.toLowerCase()) ||
        seller.specialties.some(specialty => 
          specialty.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
      
      const apparel = searchApparelSellers(searchQuery);
      
      filtered = [...jute, ...apparel];
    }

    if (selectedCity !== 'all') {
      const juteByCity = getSellersByCity(selectedCity);
      const apparelByCity = filtered.filter(s => s.city === selectedCity);
      filtered = [...juteByCity, ...apparelByCity];
    }

    if (selectedCategory !== 'all') {
      if (['Knitwear', 'Woven Garments', 'Sweaters', 'Garments', 'Apparel', 'T-shirts', 'Shirts', 'Trousers'].includes(selectedCategory)) {
        // For apparel categories
        const apparelByProduct = getApparelSellersByProduct(selectedCategory);
        const juteByProduct = getSellersByProduct(selectedCategory);
        filtered = [...juteByProduct, ...apparelByProduct];
      } else {
        // For jute and other categories
        filtered = getSellersByProduct(selectedCategory);
      }
    }

    // Sort sellers
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'name':
          return a.organization.localeCompare(b.organization);
        case 'products':
          return b.totalProducts - a.totalProducts;
        case 'established':
          return (b.yearEstablished || 0) - (a.yearEstablished || 0);
        default:
          return 0;
      }
    });

    setFilteredSellers(filtered);
  }, [searchQuery, selectedCity, selectedCategory, sortBy]);

  const SellerCard = ({ seller }: { seller: any }) => (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{seller.organization}</h3>
              {seller.verified && (
                <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs">
                  <CheckCircle className="h-3 w-3" />
                  <span>Verified</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">{seller.contactName}</p>
            <div className="flex items-center space-x-1 text-sm text-gray-600 mb-3">
              <MapPin className="h-4 w-4" />
              <span>{seller.location}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Star className="h-4 w-4 text-yellow-400 fill-current" />
            <span className="text-sm font-medium">{seller.rating}</span>
          </div>
        </div>

        <p className="text-gray-700 text-sm mb-4 line-clamp-2">{seller.description}</p>

        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {seller.specialties.slice(0, 3).map((specialty: string, index: number) => (
              <span key={index} className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                {specialty}
              </span>
            ))}
            {seller.specialties.length > 3 && (
              <span className="text-xs text-gray-500">+{seller.specialties.length - 3} more</span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
          <div className="flex items-center space-x-1">
            <Package className="h-4 w-4" />
            <span>{seller.totalProducts} products</span>
          </div>
          {seller.yearEstablished && (
            <span>Est. {seller.yearEstablished}</span>
          )}
        </div>

        <div className="flex space-x-2">
          <Link
            to={`/seller/${seller.slug}`}
            className="flex-1 bg-madeinbd-gradient text-white py-2 px-4 rounded-lg text-sm font-medium text-center hover:opacity-90 transition-opacity"
          >
            View Profile
          </Link>
          <button className="border border-green-600 text-green-600 py-2 px-4 rounded-lg text-sm font-medium hover:bg-green-50 transition-colors">
            Contact
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-madeinbd-gradient text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Find Verified Sellers</h1>
          <p className="text-xl text-orange-100 max-w-3xl mx-auto">
            Connect with trusted Bangladeshi sellers and manufacturers for your business needs
          </p>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sellers, products, or specialties..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-3 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Cities</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-3 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-3 focus:ring-2 focus:ring-orange-500 text-sm"
              >
                <option value="rating">Highest Rated</option>
                <option value="name">Name A-Z</option>
                <option value="products">Most Products</option>
                <option value="established">Oldest First</option>
              </select>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing {filteredSellers.length} sellers
            </p>
          </div>

          {filteredSellers.length > 0 ? (
            <div className={`grid gap-6 ${
              viewMode === 'grid' 
                ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1'
            }`}>
              {filteredSellers.map((seller) => (
                <SellerCard key={seller.id} seller={seller} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No sellers found</h3>
              <p className="text-gray-600">Try adjusting your search criteria to find more sellers.</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Seller Network</h2>
            <p className="text-xl text-gray-600">Connecting you with Bangladesh's finest manufacturers</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">{filteredSellers.length}+</div>
              <div className="text-gray-600">Verified Sellers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">{cities.length}+</div>
              <div className="text-gray-600">Cities Covered</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">50+</div>
              <div className="text-gray-600">Product Categories</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600 mb-2">98%</div>
              <div className="text-gray-600">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellersPage;